import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import {
    EnterpriseDto, EnterpriseLabels, getEnterpriseLabels,
    GroupSearchService,
    OrganizationDto,
    OrganizationRelocationRequestDto,
    OrganizationRelocationService,
    SecurityDomain
} from 'lib';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    Subject,
    switchMap
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    NgSelectComponent,
    NgHeaderTemplateDirective,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective
} from '@ng-select/ng-select';
import { EnterpriseLabelsFetchService } from '../../services/enterprise-labels-fetch.service';

@Component({
    selector: 'mmoga-organization-relocation',
    imports: [
        Dialog,
        NgSelectComponent,
        AsyncPipe,
        FormsModule,
        NgOptionTemplateDirective,
        NgHeaderTemplateDirective,
        NgLabelTemplateDirective
    ],
    templateUrl: './organization-relocation.component.html',
    standalone: true,
    styleUrl: './organization-relocation.component.scss'
})
export class OrganizationRelocationComponent implements OnInit {
    private readonly groupSearchService = inject(GroupSearchService);
    private readonly toastrService = inject(ToastrService);
    private readonly organizationRelocationService = inject(OrganizationRelocationService);
    private readonly enterpriseLabelsFetchService = inject(EnterpriseLabelsFetchService);

    @Input() showModal = false;
    @Input() organizationDto: OrganizationDto | undefined;
    @Input() currentEnterpriseId: number | null = null;
    @Output() isOrganizationRelocating = new EventEmitter<boolean>();

    enterprises = new Observable<EnterpriseDto[]>;
    enterpriseSearchSubject = new Subject<string>;

    selectedEnterprise: EnterpriseDto | undefined = undefined;
    enterpriseLabels: EnterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);

    ngOnInit(): void {
        this.initializeEnterpriseSearch();
        this.enterpriseLabelsFetchService.getEnterpriseLabels().then(
            labels => this.enterpriseLabels = labels
        );
    }

    initializeEnterpriseSearch(): void {
        this.enterprises = this.enterpriseSearchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(searchTerm => searchTerm?.length === 0 || searchTerm?.length > 2),
                switchMap(searchTerm => this.groupSearchService.getGroups(searchTerm, 0, 5, 'name', 'ASC')
                    .pipe(
                        map(response => {
                            return response.content.filter(enterprise => enterprise?.id !== this.currentEnterpriseId);
                        }),
                        catchError(() => {
                            this.toastrService.error('Failed to retrieve enterprises.');
                            return of([]);
                        })
                    ))
            );
    }

    createRelocation(): void {
        this.organizationRelocationService.createRelocation(this.buildRequest()).subscribe({
            next: (relocationDto) => {
                this.toastrService.success(`Started transfer of ${relocationDto.organizationDto.name} to ${relocationDto.targetEnterpriseDto.name}.`);
                this.isOrganizationRelocating.emit(true);
            },
            error: () => {
                this.toastrService.error('Failed to transfer site.');
            }
        });
    }

    buildRequest(): OrganizationRelocationRequestDto {
        return {
            sourceEnterpriseId: this.currentEnterpriseId!,
            targetEnterpriseId: this.selectedEnterprise!.id as number,
            organizationId: this.organizationDto!.id
        };
    }

    closeModal(): void {
        this.isOrganizationRelocating.emit(false);
    }
}
